import React from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';

const ExportPlanesToExcel = ({ planes, usuarios, actividades, filtros = {} }) => {
  const exportToExcel = () => {
    try {
      // Preparar datos para la hoja principal de planes
      const planesData = planes.map(plan => ({
        'ID': plan.id,
        'Título': plan.titulo,
        'Descripción': plan.descripcion || '',
        'Estado': plan.estado,
        'Prioridad': plan.prioridad,
        'Progreso (%)': parseFloat(plan.progreso_porcentaje || 0),
        'Fecha Inicio': moment(plan.fecha_inicio).format('DD/MM/YYYY'),
        'Fecha Fin': moment(plan.fecha_fin).format('DD/MM/YYYY'),
        'Responsable': `${plan.usuarioResponsable?.nombre || ''} ${plan.usuarioResponsable?.apellido || ''}`.trim(),
        'Creado por': `${plan.usuarioCreador?.nombre || ''} ${plan.usuarioCreador?.apellido || ''}`.trim(),
        'Fecha Creación': moment(plan.fecha_creacion).format('DD/MM/YYYY HH:mm'),
        'Observaciones': plan.observaciones || '',
        'Total Actividades': plan.planActividadesDetalle?.length || 0,
        'Total Lotes': plan.planLotesDetalle?.length || 0
      }));

      // Preparar datos para la hoja de actividades
      const actividadesData = [];
      planes.forEach(plan => {
        if (plan.planActividadesDetalle) {
          plan.planActividadesDetalle.forEach(actividad => {
            actividadesData.push({
              'Plan ID': plan.id,
              'Plan Título': plan.titulo,
              'Actividad': actividad.actividad?.nombre || '',
              'Subactividad': actividad.subactividad?.nombre || '',
              'Fecha Planificada': moment(actividad.fecha_planificada).format('DD/MM/YYYY'),
              'Horas Estimadas': actividad.horas_estimadas || '',
              'Estado': actividad.estado_actividad || 'pendiente',
              'Orden': actividad.orden || '',
              'Observaciones': actividad.observaciones_actividad || ''
            });
          });
        }
      });

      // Preparar datos para la hoja de lotes
      const lotesData = [];
      planes.forEach(plan => {
        if (plan.planLotesDetalle) {
          plan.planLotesDetalle.forEach(lote => {
            lotesData.push({
              'Plan ID': plan.id,
              'Plan Título': plan.titulo,
              'Lote': lote.lote?.nombre || '',
              'Sección': lote.lote?.seccion?.nombre || '',
              'Área Planificada': lote.area_planificada || '',
              'Observaciones': lote.observaciones_lote || ''
            });
          });
        }
      });

      // Preparar datos de resumen
      const totalPlanes = planes.length;
      const planesPorEstado = planes.reduce((acc, plan) => {
        acc[plan.estado] = (acc[plan.estado] || 0) + 1;
        return acc;
      }, {});
      
      const planesPorPrioridad = planes.reduce((acc, plan) => {
        acc[plan.prioridad] = (acc[plan.prioridad] || 0) + 1;
        return acc;
      }, {});

      const progresoPromedio = planes.reduce((acc, plan) => 
        acc + parseFloat(plan.progreso_porcentaje || 0), 0) / totalPlanes;

      const resumenData = [
        { 'Métrica': 'Total de Planes', 'Valor': totalPlanes },
        { 'Métrica': 'Progreso Promedio (%)', 'Valor': progresoPromedio.toFixed(2) },
        { 'Métrica': '', 'Valor': '' },
        { 'Métrica': 'ESTADOS', 'Valor': '' },
        ...Object.entries(planesPorEstado).map(([estado, cantidad]) => ({
          'Métrica': `- ${estado}`,
          'Valor': cantidad
        })),
        { 'Métrica': '', 'Valor': '' },
        { 'Métrica': 'PRIORIDADES', 'Valor': '' },
        ...Object.entries(planesPorPrioridad).map(([prioridad, cantidad]) => ({
          'Métrica': `- ${prioridad}`,
          'Valor': cantidad
        }))
      ];

      // Crear el libro de Excel
      const workbook = XLSX.utils.book_new();

      // Hoja de resumen
      const resumenWorksheet = XLSX.utils.json_to_sheet(resumenData);
      XLSX.utils.book_append_sheet(workbook, resumenWorksheet, 'Resumen');

      // Hoja principal de planes
      const planesWorksheet = XLSX.utils.json_to_sheet(planesData);
      
      // Aplicar estilos a la hoja de planes
      const planesRange = XLSX.utils.decode_range(planesWorksheet['!ref']);
      
      // Autoajustar columnas
      const columnWidths = [];
      for (let C = planesRange.s.c; C <= planesRange.e.c; ++C) {
        let maxWidth = 10;
        for (let R = planesRange.s.r; R <= planesRange.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = planesWorksheet[cellAddress];
          if (cell && cell.v) {
            const cellValue = cell.v.toString();
            maxWidth = Math.max(maxWidth, cellValue.length);
          }
        }
        columnWidths[C] = { wch: Math.min(maxWidth + 2, 50) };
      }
      planesWorksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, planesWorksheet, 'Planes de Trabajo');

      // Hoja de actividades (solo si hay datos)
      if (actividadesData.length > 0) {
        const actividadesWorksheet = XLSX.utils.json_to_sheet(actividadesData);
        XLSX.utils.book_append_sheet(workbook, actividadesWorksheet, 'Actividades');
      }

      // Hoja de lotes (solo si hay datos)
      if (lotesData.length > 0) {
        const lotesWorksheet = XLSX.utils.json_to_sheet(lotesData);
        XLSX.utils.book_append_sheet(workbook, lotesWorksheet, 'Lotes');
      }

      // Generar nombre del archivo
      const fechaActual = moment().format('YYYY-MM-DD_HH-mm');
      let nombreArchivo = `Planes_Trabajo_${fechaActual}`;
      
      // Agregar filtros al nombre si existen
      if (Object.keys(filtros).length > 0) {
        const filtrosTexto = [];
        if (filtros.estado) filtrosTexto.push(`estado-${filtros.estado}`);
        if (filtros.prioridad) filtrosTexto.push(`prioridad-${filtros.prioridad}`);
        if (filtros.usuario_responsable_id) {
          const usuario = usuarios.find(u => u.id === filtros.usuario_responsable_id);
          if (usuario) filtrosTexto.push(`responsable-${usuario.nombre}`);
        }
        if (filtrosTexto.length > 0) {
          nombreArchivo += `_${filtrosTexto.join('_')}`;
        }
      }

      // Descargar el archivo
      XLSX.writeFile(workbook, `${nombreArchivo}.xlsx`);
      
      message.success(`Archivo exportado exitosamente: ${nombreArchivo}.xlsx`);
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      message.error('Error al exportar los datos a Excel');
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportToExcel}
      disabled={!planes || planes.length === 0}
    >
      Exportar a Excel
    </Button>
  );
};

export default ExportPlanesToExcel;

